import React, { useCallback, useEffect, useState } from 'react';
import t from 'react-translate';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { translationUpdate } from 'redux/actions/users';

import { useAppDispatch } from 'redux/store';
import { getCurrentUser } from 'redux/selectors/users';
import { TranslationInfo } from 'redux/schemas/models/edit-translations';
import { closeEditTranslationComponent } from 'redux/actions/edit-translations';
import { getLectureComponent } from 'redux/selectors/timeline';
import { ExternalToolType, NLectureComponent } from 'redux/schemas/models/lecture-component';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import { gray5, white } from 'styles/global_defaults/colors';
import { doubleSpacing, halfSpacing, quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { ExternalToolWebLinkTypes } from 'redux/schemas/models/external-tool';


type EditableProps = {
  editInfo: TranslationInfo,
  onSaveRef: (fn: () => void) => void,
  setSavingDisabled: (boolean) => void,
};

const EditableThirdPartyLink = ({
  editInfo,
  onSaveRef,
  setSavingDisabled,
}: EditableProps) => {
  const lectureComponent = useSelector((state) => (
    getLectureComponent(state, editInfo.id)
  )) as NLectureComponent<ExternalToolType>;
  const { title, name, displayText, toolType, translationIds } = useSelector((state) => state.models.externalTools[lectureComponent.externalTool]);
  const initialState = {
    title,
    name,
    displayText,
  };
  const [externalLink, setExternalLink] = useState(initialState);
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useAppDispatch();

  const editableDiscussionStyle = css`
    width: 100%;
    .bs4-form-control {
      background: ${white};
    }
    .border-simple-text {
      border-radius: ${quarterSpacing}px;
      border-color: ${gray5};
    }
    margin: 0 ${doubleSpacing}px;
    .bs4-form-control {
      background: ${white};
    }
    .tag {
      margin: ${halfSpacing}px 0;
      padding: 3px 12px 3px;
      border-radius: ${halfSpacing}px;
      height: ${standardSpacing}px;
      width: fit-content;
    }
  `;

  const save = useCallback(() => {
    const { id } = editInfo;
    const attributes = [];
    const fields = Object.keys(externalLink);
    fields.forEach(field => {
      if (externalLink[field] !== initialState[field]) {
        if (translationIds[field]) {
          attributes.push({
            translationId: translationIds[field],
            name: field,
            text: externalLink[field],
            elementType: lectureComponent.type,
            elementId: lectureComponent.externalTool,
          });
        }
      }
    });
    const payload = {
      id,
      attributes,
    };
    dispatch(translationUpdate(payload)).then(() => {
      dispatch(closeEditTranslationComponent());
    });
  }, [externalLink, currentUser.translationPreferenceLanguage, dispatch, editInfo]);

  useEffect(() => {
    onSaveRef(() => save);
  }, [onSaveRef, save]);

  useEffect(() => {
    if (initialState !== externalLink) {
      setSavingDisabled(false);
    }
    return (() => {
      setSavingDisabled(true);
    });
  }, [externalLink]);


  return (
    <div className='editable-discussion' css={editableDiscussionStyle}>
      {
        externalLink.displayText && toolType !== ExternalToolWebLinkTypes.WEB_EMBED && (
          <React.Fragment>
            <div className='tag bg-gray-6 text-small font-weight-bolder'>
              {/* Depending on the weblink type, the title and the link are named differently in the FE
              even if in BE is saved correctly */}
              { toolType === ExternalToolWebLinkTypes.WEB_LINK ? t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.TITLE() : t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.DISPLAY_TEXT() }
            </div>
            <NvTextArea
              value={externalLink.displayText}
              autoComplete='off'
              textareaClassName='text-large-body border-simple-text'
              onChange={(e) => {
                setExternalLink({
                  ...externalLink,
                  displayText: e.currentTarget?.value,
                });
              }}
            />
          </React.Fragment>
        )
      }
      {
        externalLink.title && (
          <React.Fragment>
            <div className='tag bg-gray-6 text-small font-weight-bolder'>
              {/* Depending on the weblink type, the title and the link are named differently in the FE
              even if in BE is saved correctly */}
              { toolType === ExternalToolWebLinkTypes.WEB_LINK ? t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.DISPLAY_TEXT() : t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.TITLE() }
            </div>
            <NvTextArea
              value={externalLink.title}
              autoComplete='off'
              textareaClassName='text-large-body border-simple-text'
              onChange={(e) => {
                setExternalLink({
                  ...externalLink,
                  title: e.currentTarget?.value,
                  name: e.currentTarget?.value,
                });
              }}
            />
          </React.Fragment>
        )
      }
    </div>
  );
};

export default EditableThirdPartyLink;
